import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { SITE } from "../../lib/site";
import { services } from "../../data/services";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openDropdown = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setServicesOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setServicesOpen(false), 160);
  };

  const closeAll = () => {
    setOpen(false);
    setMobileServicesOpen(false);
    setServicesOpen(false);
  };

  const showServices = () => {
    closeAll();

    if (window.location.pathname === "/") {
      window.requestAnimationFrame(() => {
        document
          .getElementById("hizmetler")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  };

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/80 border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-20 sm:h-24 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center"
          aria-label="Artı Bir Yapı"
        >
          <img
            src="/assets/artibir-logo.png"
            alt="Artı Bir Yapı"
            className="h-12 sm:h-14 w-auto object-contain"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-12">
          <div
            ref={dropdownRef}
            className="relative"
            onMouseEnter={openDropdown}
            onMouseLeave={scheduleClose}
          >
            <button
              type="button"
              onClick={() => setServicesOpen((v) => !v)}
              data-testid="nav-link-hizmetler"
              aria-expanded={servicesOpen}
              aria-haspopup="true"
              className="flex items-center gap-2 text-base font-semibold text-zinc-700 hover:text-[#00844A] transition-colors relative group"
            >
              Hizmetler
              <ChevronDown
                size={14}
                className={`transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`}
              />
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-zinc-950 group-hover:w-full transition-all duration-300" />
            </button>

            {servicesOpen && (
              <div
                data-testid="nav-services-dropdown"
                className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[640px] bg-white border border-zinc-200 shadow-xl"
                onMouseEnter={openDropdown}
                onMouseLeave={scheduleClose}
              >
                <div className="grid grid-cols-2">
                  {services.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/hizmetler/${s.slug}`}
                      onClick={() => setServicesOpen(false)}
                      data-testid={`nav-service-${s.slug}`}
                      className="group flex items-start gap-4 p-5 border-b border-r border-zinc-100 hover:bg-[#00844A] transition-colors duration-300 last:border-b-0"
                    >
                      <span className="font-mono text-xs text-zinc-400 group-hover:text-white pt-1">
                        {s.number}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-sm text-zinc-950 group-hover:text-white tracking-tight">
                          {s.name}
                        </div>
                        <div className="text-xs text-zinc-500 group-hover:text-zinc-400 mt-1 line-clamp-1">
                          {s.tagline}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                <Link
                  to="/#hizmetler"
                  onClick={showServices}
                  className="flex items-center justify-between px-5 py-4 bg-zinc-50 border-t border-zinc-200 text-xs font-bold text-zinc-950 hover:bg-zinc-950 hover:text-white transition-colors"
                >
                  <span>Tüm hizmetleri görüntüle</span>
                  <span>→</span>
                </Link>
              </div>
            )}
          </div>

          <a
            href="/#surec"
            data-testid="nav-link-surec"
            className="text-base font-semibold text-zinc-700 hover:text-[#00844A] transition-colors relative group"
          >
            Süreç
            <span className="absolute -bottom-1 left-0 w-0 h-px bg-zinc-950 group-hover:w-full transition-all duration-300" />
          </a>
          <a
            href="/#iletisim"
            data-testid="nav-link-iletisim"
            className="text-base font-semibold text-zinc-700 hover:text-[#00844A] transition-colors relative group"
          >
            İletişim
            <span className="absolute -bottom-1 left-0 w-0 h-px bg-zinc-950 group-hover:w-full transition-all duration-300" />
          </a>
        </nav>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          className="md:hidden w-10 h-10 flex items-center justify-center text-zinc-950"
          aria-label="Menüyü Aç"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-black/5 bg-white max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="nav-mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            <button
              type="button"
              onClick={() => setMobileServicesOpen((v) => !v)}
              className="flex items-center justify-between text-base font-medium text-zinc-800 py-3"
              data-testid="nav-mobile-hizmetler"
              aria-expanded={mobileServicesOpen}
            >
              Hizmetler
              <ChevronDown
                size={16}
                className={`transition-transform duration-200 ${mobileServicesOpen ? "rotate-180" : ""}`}
              />
            </button>
            {mobileServicesOpen && (
              <div className="flex flex-col pl-2 pb-2 border-l border-zinc-200 ml-2">
                {services.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/hizmetler/${s.slug}`}
                    onClick={closeAll}
                    data-testid={`nav-mobile-service-${s.slug}`}
                    className="flex items-center justify-between py-2.5 pl-3 text-sm text-zinc-700 hover:text-zinc-950"
                  >
                    <span>{s.name}</span>
                    <span className="text-xs font-mono text-zinc-400">{s.number}</span>
                  </Link>
                ))}
                <Link
                  to="/#hizmetler"
                  onClick={showServices}
                  className="mt-2 py-2 pl-3 text-xs font-bold text-zinc-950"
                >
                  Tüm hizmetleri görüntüle →
                </Link>
              </div>
            )}
            <a
              href="/#surec"
              onClick={closeAll}
              className="text-base font-medium text-zinc-800 py-3 border-t border-zinc-100"
            >
              Süreç
            </a>
            <a
              href="/#iletisim"
              onClick={closeAll}
              className="text-base font-medium text-zinc-800 py-3 border-t border-zinc-100"
            >
              İletişim
            </a>
            <a
              href={`tel:${SITE.phoneTel}`}
              className="mt-3 inline-flex items-center justify-center gap-2 px-5 py-3 bg-zinc-950 text-white text-sm font-bold"
            >
              <Phone size={15} strokeWidth={2.5} />
              {SITE.phoneDisplay}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
